import React, {useState, useEffect, useRef, useCallback} from "react";
import { Outlet } from "react-router-dom";
import "../styles/app.scss";
import { useSelector } from "react-redux";
import {
  selectAdminUuid,
  selectAccessToken,
  selectImpersonatorUuid,
  selectImpersonatorEmail,
  impersonationEnded,
} from "../features/auth/authSlice";
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getRoleJSON, useIsMobile } from "./utils";
import { logout } from '../features/auth/authSlice';
import {
  useLoadClaudeAccessStatusQuery,
  useListAiActivityQuery,
  useImpersonateReturnMutation,
  useLoadMyReportsQuery,
  useListJobOpeningsQuery,
  useListManagedJobOpeningsQuery,
  useListMyTrainingCoursesQuery,
  useListMyFormsQuery,
  useGetFormsTeamStatusQuery,
  useGetAdminConfigQuery,
  useGetCalendarAccessQuery,
  useGetEventsCalendarStatusQuery,
  useGetMarketingLibraryQuery,
  useGetResourcePagesQuery,
} from '../app/api';
import FeedbackWidget from './FeedbackWidget';
import UserSettingsModal from './UserSettingsModal';

// Every leaf `path` under a menu tree, submenus included. Module scope because
// the mode computation needs it before the menu arrays are picked.
const collectLeafPaths = (items) => items.flatMap(i =>
  i.submenu ? collectLeafPaths(i.submenu) : [i.path]
);

export default function Root() {
  const isMobile = useIsMobile();
  // Default the sidebar to collapsed on mobile so the page has room. On
  // desktop it stays expanded. Users can still toggle in either mode.
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => !isMobile);
  // Personal settings dialog, opened from the gear in the sidebar footer.
  const [showSettings, setShowSettings] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  // The Sidebar is declared inside this component, so it remounts on every
  // Root render (including on navigation) — which would snap the menu's scroll
  // position back to the top. We record the menu's scrollTop as the user
  // scrolls and restore it whenever the scroll container remounts, so clicking
  // a sub-item keeps your place in a long menu. The ref lives on Root (which
  // does not remount), so the value survives the Sidebar's remounts.
  const menuScrollRef = useRef(0);
  const setMenuWrapperRef = useCallback((el) => {
    if (el) el.scrollTop = menuScrollRef.current;
  }, []);
  const onMenuScroll = useCallback((e) => {
    menuScrollRef.current = e.currentTarget.scrollTop;
  }, []);

  // Snap sidebar state when crossing the breakpoint so users don't land in an
  // unusable layout (e.g. expanded sidebar on a phone after rotating).
  useEffect(() => {
    setIsSidebarExpanded(!isMobile);
  }, [isMobile]);

  // The sidebar itself reflects `isSidebarExpanded` directly so the user can
  // open it on mobile to reach submenu items. But the page content always
  // sits behind the 5rem collapsed rail on mobile — the expanded sidebar
  // overlays content rather than pushing it.
  const contentExpanded = isSidebarExpanded && !isMobile;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const thisUUID = useSelector(selectAdminUuid);
  const accessToken = useSelector(selectAccessToken);
  const userEmail = window.localStorage.getItem("e");
  const impersonatorUuid = useSelector(selectImpersonatorUuid);
  const impersonatorEmail = useSelector(selectImpersonatorEmail);
  const isImpersonating = Boolean(impersonatorUuid);
  const [impersonateReturn, { isLoading: isReturning }] = useImpersonateReturnMutation();

  const handleReturnToAdmin = async () => {
    try {
      const res = await impersonateReturn().unwrap();
      dispatch(impersonationEnded({
        accessToken: res.access_token,
        adminUuid: res.uuid,
        email: res.username,
      }));
      // Drop them back on the admin page so they immediately see where they left off.
      window.location.assign('/admin');
    } catch (_err) {
      // Fall back to a full logout if the swap fails; safer than leaving the
      // session in a half-state where the user can't tell which identity they hold.
      dispatch(logout());
      window.location.assign('/login');
    }
  };

  const [roleJSON, setRoleJSON] = useState({ roles: null, status: null, isAdmin: false, isTech: false, isHR: false, isCompliance: false, isExec: false, isFinance: false, isFintech: false, isEntrepreneur: false, isMarketing: false, isConfidentialReviewer: false, isEmployee: false });

  // Per-user Claude certification status — drives the status indicator next
  // to the "Claude Certification" sidebar item (and its parent "To-Do" group).
  // `submitted_at` is populated once the user has confirmed policies,
  // confirmed training, and uploaded a certificate.
  const claudeStatus = useLoadClaudeAccessStatusQuery(undefined, { skip: !thisUUID });
  const claudeStatusLoaded = claudeStatus.data !== undefined;
  const isClaudeCertified = Boolean(claudeStatus.data?.submitted_at);

  // AI Activity catalog — drives the video-watch indicator. Each item's
  // completion status arrives in the response, so we just look up by key.
  const aiActivity = useListAiActivityQuery(undefined, { skip: !thisUUID });
  const aiActivityLoaded = aiActivity.data !== undefined;
  const isVideoConfirmed = (aiActivity.data || []).some(
    a => a.activity_key === 'claude_projects_video' && a.status === 'completed'
  );

  // Assigned training — anything not yet passed is an outstanding to-do. Drives
  // the "Training" item's status and feeds the "To-Do" group count.
  const myTraining = useListMyTrainingCoursesQuery(undefined, { skip: !thisUUID });
  const trainingLoaded = myTraining.data !== undefined;
  const pendingTrainingCount = (myTraining.data || []).filter(c => c.status !== 'passed').length;
  const hasTrainingDue = pendingTrainingCount > 0;

  // Forms HR has granted the current employee, each carrying a `submitted` flag.
  // A granted-but-unsubmitted form is an outstanding to-do, exactly like an
  // unfinished Claude step or unpassed course. Loaded separately below so the
  // To-Do rollup can include it.
  const myForms = useListMyFormsQuery(undefined, { skip: !thisUUID });
  const formsLoaded = myForms.data !== undefined;
  const pendingFormsCount = (myForms.data?.forms || []).filter(f => !f.submitted).length;

  // Count of outstanding To-Do items, shown as a badge on the group. Each
  // unfinished Claude step counts as one; each unpassed training course counts
  // as one; each granted-but-unsubmitted form counts as one. Signals still
  // loading are simply not counted yet.
  const todoCount =
    (claudeStatusLoaded && !isClaudeCertified ? 1 : 0) +
    (aiActivityLoaded && !isVideoConfirmed ? 1 : 0) +
    (trainingLoaded ? pendingTrainingCount : 0) +
    (formsLoaded ? pendingFormsCount : 0);

  // Manager status comes from the company directory (`is_manager`), not from
  // a role — so it drives the "My Team" nav item directly rather than going
  // through roleJSON. Non-managers get `is_manager: false` and the item hides.
  const myReports = useLoadMyReportsQuery(undefined, { skip: !thisUUID });
  const isManager = Boolean(myReports.data?.is_manager);

  // Job openings the current user is entitled to see (server filters by the
  // per-posting visibility tier). Drives whether the main-menu "Job Openings"
  // item shows at all — we hide it when there's nothing the user can view.
  const jobOpenings = useListJobOpeningsQuery(undefined, { skip: !thisUUID });
  const hasViewableJobOpenings = (jobOpenings.data?.openings?.length || 0) > 0;

  // The IT Support link (Super Admin → Config). When set, an "IT Support"
  // button shows in the sidebar below Career Opportunities and opens it in a
  // new tab; hidden when blank.
  const { data: adminConfig } = useGetAdminConfigQuery();
  const itSupportUrl = adminConfig?.config?.it_support_url || '';

  // "Your Day" is feature-flagged as well as BETA-gated, and the flag is the kill
  // switch — so when it's off the nav item and the URL both have to disappear.
  // Showing the item and letting the page 404 made the feature look broken rather
  // than switched off. `undefined` while the config query is in flight is
  // deliberately distinguished from `false` below, so a hard refresh onto
  // /your-day doesn't bounce before the flag has loaded.
  // The floating "Hub Feedback" launcher (Super Admin → Config). Note the
  // inverted test compared with the flags below: this one defaults to ON, so we
  // hide only on an explicit `false`. Treating the still-loading `undefined` as
  // off would make the button flash in on every hard refresh for everyone, which
  // is the common case.
  const feedbackButtonHidden = adminConfig?.config?.show_feedback_button === false;

  const yourDayEnabled = adminConfig?.config?.enable_your_day;
  const meetingsEnabled = adminConfig?.config?.enable_meetings;
  const plannerEnabled = adminConfig?.config?.enable_planner;
  const tasksEnabled = adminConfig?.config?.enable_tasks;
  // Same strict !== true treatment: while the config query is in flight the value
  // is undefined, and treating that as "on" would flash the item in and out.
  const calendarEnabled = adminConfig?.config?.enable_calendar;
  // On top of the kill switch, Calendar is granted person by person via the
  // "Show Calendar" box on their directory entry — the page writes to real
  // shared calendars and emails attendees, so it goes to the admins who support
  // advisors rather than to everyone. Skipped entirely while the feature is off
  // or the config is still loading, so most employees never make this call.
  const calendarAccess = useGetCalendarAccessQuery(undefined, {
    skip: !thisUUID || calendarEnabled !== true,
  });
  // Same strict !== true as the flag: undefined means "still asking", and
  // treating that as granted would flash the item in and then remove it.
  const calendarAllowed = calendarAccess.data?.allowed === true;
  // The Events row shows once HR has pointed it at a calendar. Whether each
  // person can actually read that calendar is Exchange's call, made on the page.
  const eventsCalendar = useGetEventsCalendarStatusQuery(undefined, { skip: !thisUUID });
  const eventsConfigured = eventsCalendar.data?.configured === true;

  // The manager-side Team Role Alignment page. Off until HR opens the review
  // window, and off is the default — a manager reading their reports' candid
  // answers early is the thing this switch exists to prevent.
  const teamRoleAlignmentEnabled = adminConfig?.config?.enable_team_role_alignment === true;

  // Managerial Assessments the current manager personally owes — the same
  // `due_count` the Team Role Alignment page shows in its warning banner, hoisted
  // into the sidebar so a manager sees the number without opening the page.
  // Skipped for non-managers: the endpoint would just return an empty team, and
  // the nav item is hidden for them anyway. Skipped while the window is closed
  // too, which is what stops the sidebar badging assessments nobody is being
  // asked for yet (the endpoint returns nothing then either way).
  const teamFormStatus = useGetFormsTeamStatusQuery(undefined, {
    skip: !thisUUID || !isManager || !teamRoleAlignmentEnabled,
  });
  const roleAlignmentDueCount = teamFormStatus.data?.due_count || 0;

  // Postings the current user is the hiring manager for (active + unfilled).
  // Drives the "My Job Applicants" nav item — available to any manager who has
  // an open posting, no HR/admin role required. Hidden when they have none.
  const managedOpenings = useListManagedJobOpeningsQuery(undefined, { skip: !thisUUID });
  const hasManagedOpenings = (managedOpenings.data?.openings?.length || 0) > 0;

  // Each granted form becomes a leaf under the To-Do → Forms submenu (the group
  // hides itself when none are granted). Carrying statusKey/activityKey lets the
  // shared To-Do status rollup flag an unsubmitted form, just like Claude/training.
  /* The collateral library's sections, as nav children.
   *
   * Built from the API rather than hard-coded because Marketing can add,
   * rename, reorder and unpublish sections from their own admin page with no
   * deploy — a fixed list here would be wrong the first time they did.
   *
   * `slug` and not `id` in the URL: a slug is fixed at creation (it is the S3
   * prefix, which is why it can never change), so a bookmarked section link
   * survives a rename. The response is already ordered.
   */
  const marketingLibrary = useGetMarketingLibraryQuery(undefined, { skip: !thisUUID });
  const marketingSectionItems = (marketingLibrary.data?.categories || []).map((c) => ({
    employee: true,
    icon: c.icon || 'collection',
    label: c.name,
    path: `/marketing-resources/${c.slug}`,
  }));

  /* The Resource Library's own pages, as nav children — siblings of the
   * Marketing group inside the same sidebar section.
   *
   * Built from the API for the same reason as the marketing sections above: the
   * MARKETING team add, rename, reorder and unpublish these from Admin →
   * Marketing → Resource Library with no deploy, so a fixed list here would be
   * wrong the first time they did.
   *
   * `slug` and not `id` in the URL, again for the same reason — a page's slug is
   * fixed at creation (it is also its storage folder), so a bookmark survives a
   * rename. The response is already ordered.
   */
  const resourceLibrary = useGetResourcePagesQuery(undefined, { skip: !thisUUID });

  /* The pages, folded into their folders.
   *
   * A page carries `group_id` (null for one that sits directly under Resource
   * Library, which is where they all sat before folders existed) and the payload
   * carries the folders themselves. The server has already dropped any folder
   * holding none of THIS reader's pages, so there is no "would this unfold onto
   * nothing" test to make here.
   *
   * FOLDERS FIRST, THEN THE LOOSE PAGES. The group already opens with the
   * Marketing folder, so keeping the collapsible rows contiguous means the menu
   * reads as a short list of folders followed by one block of direct links,
   * rather than links split around headings.
   *
   * A page whose `group_id` names a folder that is not in the payload falls
   * through to the ungrouped block. That is the deliberate failure mode for a
   * deleted group (there is no FK) — the page stays reachable at the top level
   * instead of disappearing into a folder nothing renders.
   */
  // No `icon`: everything in the Resource Library sits in the sidebar as plain
  // text at the first level and as a bullet below that. The per-page icon is
  // still chosen in the admin and still rendered - on the page's own header,
  // not out here, where a column of near-identical document glyphs was noise.
  const resourceLeaf = (p) => ({
    employee: true,
    label: p.name,
    path: `/resources/${p.slug}`,
  });
  const resourceGroups = resourceLibrary.data?.groups || [];
  const resourcePages = resourceLibrary.data?.pages || [];
  const groupIds = new Set(resourceGroups.map((g) => g.id));
  const resourcePageItems = [
    ...resourceGroups.map((g) => ({
      // `menuKey` because nothing stops Marketing naming two folders the same
      // thing, and the menu keys entries by their label path — two "Reports"
      // folders would otherwise share one React key and one expanded/collapsed
      // bit, and open and close together.
      menuKey: `group:${g.id}`,
      label: g.name,
      submenu: resourcePages.filter((p) => p.group_id === g.id).map(resourceLeaf),
    })),
    ...resourcePages
      .filter((p) => p.group_id == null || !groupIds.has(p.group_id))
      .map(resourceLeaf),
  ];

  const myFormItems = (myForms.data?.forms || []).map((f) => ({
    employee: true, icon: 'file-earmark-text', label: f.label, path: f.path,
    statusKey: 'todo', activityKey: 'form', done: Boolean(f.submitted),
  }));

  const assignRoleJSON = async(thisUUID) => {
    if (thisUUID) {
      const roleData = await getRoleJSON(thisUUID);
      setRoleJSON(roleData);
    }
  }
  useEffect(() => {
    if (thisUUID){
      assignRoleJSON(thisUUID);
    }
    
  },[thisUUID]);

  // Route-level access control. Each entry maps a path prefix to a predicate
  // over the user's role. Direct URL navigation to any of these paths by a
  // user without the required role is bounced to /dashboard. Paths not listed
  // here (e.g. /dashboard, /claude-access, /employee-directory, /status) are
  // open to every authenticated user.
  const routePermissions = [
    // Reads advisors' Agentic WM conversations - SUPER_ADMIN, not ADMIN.
    { prefix: '/admin/agentic-wm', allow: r => r.isSuperAdmin },
    { prefix: '/admin',       allow: r => r.isAdmin },       // ADMIN
    { prefix: '/cron',        allow: r => r.isAdmin },       // ADMIN
    // Role Alignment is shared with CONFIDENTIAL_REVIEWER, who reads the answers
    // there — listed before the generic /tech/ rule (find() takes the first match).
    { prefix: '/tech/forms/role-alignment', allow: r => r.isTech || r.isConfidentialReviewer },
    { prefix: '/tech/',       allow: r => r.isTech },        // TECH / ADMIN
    { prefix: '/hr/',         allow: r => r.isHR },          // HR / ADMIN
    // Reg S-P lives under /compliance/ but is a shared workspace for the three
    // due-diligence teams — list it before the generic /compliance/ rule (find()
    // returns the first match).
    { prefix: '/compliance/reg-sp', allow: r => r.isCompliance || r.isTech || r.isFintech },
    { prefix: '/compliance/', allow: r => r.isCompliance },  // COMPLIANCE / ADMIN
    { prefix: '/executive/',  allow: r => r.isExec },        // EXEC / ADMIN
    // BETA (early access) *and* the enable_your_day kill switch.
    { prefix: '/your-day',    allow: r => r.isEmployee && yourDayEnabled === true },
    // Meetings Assistant: same BETA + kill-switch pair as Your Day, since it
    // reads the same mailbox and calendar.
    { prefix: '/meetings',    allow: r => r.isEmployee && meetingsEnabled === true },
    // Planner summary: same BETA + kill-switch pair as the two above.
    { prefix: '/planner',     allow: r => r.isEmployee && plannerEnabled === true },
    // Tasks: same BETA + kill-switch pair. Note this one WRITES to M365, so
    // the kill switch is doing more work here than on the read-only pages.
    { prefix: '/tasks',       allow: r => r.isEmployee && tasksEnabled === true },
    // Bloom rides Your Day's flag, not its own: the API sits behind the same
    // pilot gate as the briefing, and `enable_bloom_tile` is a separate switch
    // the PAGE honours by showing "Bloom isn't available" rather than a bounce.
    { prefix: '/bloom',       allow: r => r.isEmployee && yourDayEnabled === true },
    // Kill switch AND the per-person "Show Calendar" grant on their directory
    // entry — being an employee is not enough on its own.
    { prefix: '/calendar',    allow: r => r.isEmployee && calendarEnabled === true && calendarAllowed },
    { prefix: '/finance/',    allow: r => r.isFinance },     // FINANCE / ADMIN
    // More specific first (find() returns the first match): the config page is
    // ADMIN + ENTREPRENEUR, the rest of /entrepreneur is ENTREPRENEUR.
    { prefix: '/entrepreneur/config', allow: r => r.isEntrepreneur && r.isAdmin }, // ENTREPRENEUR + ADMIN
    { prefix: '/entrepreneur', allow: r => r.isEntrepreneur }, // ENTREPRENEUR
    // Trailing slash is load-bearing: '/marketing-resources' is the read-only
    // view every employee may see, and it startsWith('/marketing'). Only
    // '/marketing/...' is the MARKETING-gated section.
    { prefix: '/marketing/', allow: r => r.isMarketing },   // MARKETING
    // Work: WORK gets you onto the side (and its Home dashboard); each area
    // then needs its own role, and pages still being built also need BETA.
    // Mirrors the `work` items in workMenu below. Order matters — find() takes
    // the first match, so every specific prefix sits above its parent.
    //
    // A Salesforce household's DETAIL page stays open to all of WORK: Portal
    // Clients and CRM both link to it for a client's linked AUM household.
    // Only the Salesforce Clients list is in beta.
    { prefix: '/work/clients/fsc/', allow: r => r.isWork },
    { prefix: '/work/clients/fsc',  allow: r => r.isWork && r.isBeta },
    { prefix: '/work/clients',      allow: r => r.isWork && r.isWorkPortalClients },
    { prefix: '/work/documents',    allow: r => r.isWork && r.isWorkDocuments },
    { prefix: '/work/crm/calendar', allow: r => r.isWork && r.isWorkCrm && r.isBeta },
    { prefix: '/work/crm',          allow: r => r.isWork && r.isWorkCrm },
    { prefix: '/work/agentic-wm',   allow: r => r.isWork && r.isWorkCrm && r.isBeta },
    { prefix: '/work/tasks',        allow: r => r.isWork && r.isWorkTasks && r.isBeta },
    { prefix: '/work/reports',      allow: r => r.isWork && r.isBeta },
    { prefix: '/work',              allow: r => r.isWork },        // WORK
  ];

  useEffect(() => {
    // Wait until roles have actually been fetched. roleJSON.roles starts as
    // null and is replaced by an array once `getRoleJSON` resolves; using
    // null as the sentinel avoids bouncing newly-loaded admins with an
    // empty role list off pages before their data is ready.
    if (roleJSON?.roles === null) return;
    // Same reasoning for the admin config: a rule can now depend on a feature
    // flag (/your-day), and `undefined` while the query is in flight would look
    // like "disabled" and bounce a permitted user off a deep link on refresh.
    if (adminConfig === undefined) return;
    // And the same again for the per-person Calendar grant, which is a second
    // query. Only worth waiting on when the feature is actually on — when it's
    // off the query is skipped and never resolves, and bouncing is correct.
    if (calendarEnabled === true && calendarAccess.data === undefined) return;
    const match = routePermissions.find(rp => location.pathname.startsWith(rp.prefix));
    if (match && !match.allow(roleJSON)) {
      navigate('/dashboard');
    }
  }, [roleJSON, location.pathname, adminConfig, calendarEnabled, calendarAccess.data]);

  const isKYBForm = () => {
    return location.pathname.includes('/KYB');
  }

  useEffect(() => {
    if (accessToken) {
      try {
        const tokenData = JSON.parse(atob(accessToken.split('.')[1]));
        const expirationTime = tokenData.exp * 1000;
       
        if (Date.now() >= expirationTime) {
          dispatch(logout());
        } else {
          const timeoutId = setTimeout(function(){console.log('logging out due to timeout');dispatch(logout());}, expirationTime - Date.now());
          return () => clearTimeout(timeoutId);
        }
      } catch (error) {
        dispatch(logout());
      }
    }
  },[accessToken]);

  /*
  const Header = () => (
    <header className={`top-header ${isSidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
      <div className="container-fluid h-100">
        <div className="row h-100 align-items-center">
          <div className="col">
          </div>
          <div className="col-auto">
            <div className="d-flex align-items-center gap-3">
              <div className="position-relative">
              </div>
              <div className="rounded-circle bg-secondary" style={{ width: '2rem', height: '2rem' }}></div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
  */

  const toggleMenu = (menuLabel) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuLabel]: !prev[menuLabel]
    }));
  };

  const sendMenuClick = (path) => {
    navigate(path);
    // On mobile, the expanded sidebar overlays page content; close it once the
    // user has picked something so they're not left with the menu covering the
    // page they just navigated to.
    if (isMobile) setIsSidebarExpanded(false);
  };

  // Mode is URL-driven, and the URL is the only source of truth for it — there
  // is no mode state to fall out of sync with the address bar on a back button
  // or a pasted link. `/work/*` is Work; anything whose path belongs to My Desk
  // is Desk; everything else is Hub.
  //
  // Desk membership is DERIVED from deskMenu's own leaf paths rather than a
  // hand-kept list, so adding a page to that menu is the only edit needed — a
  // second list would drift the first time someone forgot it.
  const isWorkMode = location.pathname.startsWith('/work');

  const currentYear = new Date().getFullYear();

  /* THREE MODES, not two.
   *
   * The sidebar used to hold everything — 13 personal rows, then 8 department
   * groups — which for anyone holding several roles was 21 rows and three
   * screens of scrolling. It is split by WHOSE work it is:
   *
   *   Hub      the company: announcements, directory, holidays, and the
   *            department consoles that run it
   *   My Desk  my own day: briefing, meetings, tasks, calendar, my to-dos
   *   Work     the wealth-management workspace (unchanged)
   *
   * The split degrades by role, which is the point: a typical employee sees Hub
   * as five rows, because the department groups they can't open were never
   * rendered anyway.
   *
   * The department consoles live in HUB rather than a fourth "Admin" mode. A
   * fourth pill would force calls like "is Compliance Training under Admin or
   * Hub?", and most people would see it empty. "The company" covers both
   * reading about it and running it.
   */
  const deskMenu = [
    {
      // AI morning briefing over the user's own Outlook inbox + calendar. First
      // item in the mode because it is the start-of-day landing page.
      // Gated on BETA (the early-access group) so the pilot audience is managed
      // by assigning a role in the Admin Panel, not by editing this file.
      // Widening to everyone later is `employee: true` here plus PILOT_ROLES in
      // routers/your_day.py.
      // Menu label only. The PAGE is still "Your Day" in its own header and at
      // /your-day — this reads better in a list next to Meetings and Calendar,
      // where "Your" is the word carrying the least.
      label: 'At A Glance',
      icon: 'sunrise-fill',
      path: '/your-day',
      employee: true,
      requiresContent: 'yourDay',
    },
    {
      // Meetings Assistant. Same BETA gating rationale as Your Day above: the
      // pilot audience is managed by assigning a role, not by editing this file.
      label: 'Meetings',
      icon: 'calendar-check-fill',
      path: '/meetings',
      employee: true,
      requiresContent: 'meetings',
    },
    {
      // Planner summary. Same BETA gating rationale as the two above; sits next
      // to them because it answers the third "where do I stand" question after
      // mail and meetings.
      label: 'Planner',
      icon: 'kanban-fill',
      path: '/planner',
      employee: true,
      requiresContent: 'planner',
    },
    {
      // Bloom Growth rocks and to-dos, in the slot Tasks used to hold — Tasks
      // moved onto At A Glance as its third tab, and Bloom came the other way
      // out of that page's third tab. `enable_bloom_tile` decides whether the
      // integration runs; `enable_your_day` is what this item follows, because
      // the API sits behind the same gate as the briefing.
      label: 'Bloom',
      icon: 'flower1',
      path: '/bloom',
      employee: true,
      requiresContent: 'yourDay',
    },
    {
      // Shared Outlook calendars. Employee-wide (Exchange decides what each
      // person can actually see), but hidden behind the feature flag because it
      // can write to real calendars.
      label: 'Calendar',
      icon: 'calendar3',
      path: '/calendar',
      employee: true,
      requiresContent: 'calendar',
    },
    // Managers get a "My Team" group: the two things they do about their team
    // (watch its Claude spend, write its Role Alignment assessments) sit under
    // one collapsible rather than as two unrelated top-level rows. Individual
    // contributors have only their own usage to look at, so for them it stays a
    // plain link labelled "My Usage" — a one-item group would be noise.
    isManager
      ? {
          label: 'My Team',
          icon: 'diagram-3-fill',
          // Assessments due from this manager, badged on the group like the To-Do
          // count so the number shows without expanding. Nothing due shows
          // nothing — no "all clear" check; the point is surfacing outstanding
          // work, not completion.
          countKey: 'roleAlignment',
          submenu: [
            { employee: true, icon: 'person-badge-fill', label: 'Claude Usage', path: '/my-team' },
            // Role Alignment status for the team, and the Managerial Assessment
            // owed per direct report. The page copes with an empty team, and the
            // API enforces who may read whose answers.
            {
              managerOnly: true,
              icon: 'clipboard-check-fill',
              label: 'Role Alignment',
              path: '/forms/team-role-alignment',
              countKey: 'roleAlignment',
              // Hidden until HR opens the review window (Super Admin → Config).
              // The API closes at the same time, so the URL isn't a way round it.
              requiresContent: 'teamRoleAlignment',
            },
          ],
        }
      : {
          label: 'My Usage',
          icon: 'person-badge-fill',
          path: '/my-team',
          employee: true,
        },
    {
      label: 'To-Do',
      icon: 'check2-square',
      statusKey: 'todo',
      countKey: 'todo',
      submenu: [
        // Forms HR has granted to this employee. Sits above AI; hidden entirely
        // when the user has no granted forms.
        {
          label: 'Forms',
          icon: 'ui-checks',
          statusKey: 'todo',
          submenu: myFormItems,
        },
        {
          label: 'AI',
          icon: 'robot',
          statusKey: 'todo',
          submenu: [
            { employee: true, icon: 'patch-check', label: 'Claude Certification', path: '/claude-access',          statusKey: 'todo', activityKey: 'claudeCert' },
            { employee: true, icon: 'play-btn',    label: 'Claude Projects',      path: '/claude-projects-video', statusKey: 'todo', activityKey: 'videoConfirm' },
          ],
        },
        { employee: true, icon: 'mortarboard', label: 'Training', path: '/training', statusKey: 'todo', activityKey: 'training' },
      ],
    },
    {
      /* Everything reference-shaped, in one place.
       *
       * Two kinds of content live under here, which is why Marketing is a
       * nested group rather than a peer of its own sections:
       *
       *   Resource Library
       *     Marketing            <- the B2B collateral library, one child per
       *       Monthly Toolkit       section, laid out the way the entrepreneur
       *       Presentation …        firms see it in their own hub
       *       …
       *     Salesforce           <- a resource_groups folder, holding the
       *       How-Tos               reference pages filed into it
       *       Reporting
       *     IT Onboarding        <- a page in no folder, where every reference
       *     …                       page sat before folders existed
       *
       * Marketing is not one of those folders and cannot be moved into one: it
       * is a different feature over a different table, nested here so a person
       * looking for reference material finds both kinds in one place.
       *
       * Keeping the marketing sections nested under one "Marketing" entry is
       * what makes room for the reference pages without a flat list of a dozen
       * unrelated siblings, and it keeps every /marketing-resources/<slug>
       * bookmark working — the leaf paths are unchanged.
       *
       * MARKETING now authors both halves (the reference pages moved over from
       * TECH on 2026-09-18), so the split here is by AUDIENCE, not by team: the
       * nested group is collateral the firms see, the siblings are internal
       * pages that a separate checkbox can additionally publish to the firms.
       *
       * This lives on DESK, not Hub, because it is material you USE. Authoring
       * is a different job in both cases and lives in Admin, under Marketing:
       * Marketing Resources and Resource Library, both MARKETING-gated.
       *
       * The group and each subgroup hide themselves when empty or still
       * loading: hasAccess on a group is "any child is accessible", and there
       * are no children until the fetch lands. So a company with no reference
       * pages sees exactly what it sees today, one level deeper.
       */
      label: 'Resource Library',
      icon: 'collection',
      submenu: [
        {
          // No icon, to sit level with the Resource Library's own folders
          // beside it.
          label: 'Marketing',
          submenu: marketingSectionItems,
        },
        ...resourcePageItems,
      ],
    },
    {
      label: 'My Job Applicants',
      icon: 'person-lines-fill',
      path: '/job-applicants',
      // Shown to any employee who is the hiring manager on an active, unfilled
      // posting — no HR/admin role required. Hidden once they have none.
      requiresContent: 'managedOpenings',
    },
  ];

  const hubMenu = [
    {
      label: 'Hub Dashboard',
      icon: 'house-fill',
      path: '/dashboard',
      employee: true,
    },
    {
      label: 'Employee Directory',
      icon: 'people-fill',
      // Was '/dashboard#directory' when the directory was a section of the hub.
      // It now has its own page, which freed the bottom third of the dashboard
      // for the Quick Links / Follow Us row.
      path: '/employee-directory',
      employee: true,
    },
    {
      label: `${currentYear} Holidays`,
      icon: 'calendar3',
      path: '/holidays',
      employee: true,
    },
    {
      // The firm's events calendar, read from a shared Outlook calendar.
      label: 'Upcoming Events',
      icon: 'calendar-event',
      path: '/events',
      employee: true,
      requiresContent: 'events',
    },
    {
      label: 'Career Opportunities',
      icon: 'briefcase',
      path: '/job-openings',
      employee: true,
      // Hidden unless the user actually has at least one viewable posting.
      requiresContent: 'jobOpenings',
    },
  ];

  /* The department consoles — the things you OPERATE rather than read.
   *
   * Their own mode, reached from the full-width Backstage button under the
   * pills — the machinery behind the show employees see, which is a truer name
   * than "Admin" and avoids colliding with the ADMIN role (a specific thing
   * that gates Configuration, and which most people in here do not hold).
   * because they are a different kind of thing from Hub/Desk/Work and because
   * leaving them in Hub put eight collapsed groups under five real rows for
   * anyone holding roles. Hub is now five rows for everybody.
   *
   * Each group is still role-gated individually, so this mode shows only the
   * consoles the person can actually open, and the button hides entirely when
   * that is none — which for most of the company is the case.
   *
   * The old `{ sectionHeader: 'ADMIN' }` divider that used to separate these
   * from the employee pages is gone: the mode itself is the divider now.
   */
  const backstageMenu = [
    {
      label: 'HR Portal',
      icon: 'people-fill',
      submenu: [
        { hr: true, icon: 'megaphone',          label: 'HR Announcements',         path: '/hr/announcements' },
        { hr: true, icon: 'pencil-square',      label: 'Manage Directory',         path: '/hr/directory' },
        { hr: true, icon: 'diagram-3',          label: 'Org Chart',                path: '/hr/org-chart' },
        { hr: true, icon: 'briefcase-fill',     label: 'Career Opportunities',     path: '/hr/job-openings' },
        { hr: true, icon: 'calendar-event',     label: 'Holidays & Summer Hours',  path: '/hr/holidays' },
        { hr: true, icon: 'sliders',            label: 'Config',                   path: '/hr/config' },
      ],
    },
    {
      label: 'Compliance',
      icon: 'shield-check',
      submenu: [
        { compliance: true, icon: 'robot',           label: 'Claude Activations', path: '/compliance/claude-activations' },
        { compliance: true, icon: 'building-gear',   label: 'B2B Claude Activations', path: '/compliance/b2b-claude-activations' },
        { compliance: true, icon: 'mortarboard',     label: 'Training Courses',   path: '/compliance/training' },
        { compliance: true, icon: 'clipboard-data',  label: 'Training Results',   path: '/compliance/training-results' },
        // Reg S-P software register. Compliance owns the page, but the Fintech
        // and IT (TECH) teams do their due-diligence sections here too, so it's
        // visible to all three roles.
        { compliance: true, tech: true, fintech: true, icon: 'shield-lock', label: 'Reg S-P', path: '/compliance/reg-sp' },
        { compliance: true, icon: 'clipboard2-check', label: 'Quick Hitter', path: '/compliance/quick-hitter' },
      ],
    },
    {
      label: 'Tech',
      icon: 'cpu',
      submenu: [
        // Hidden for now — restore when ready.
        // { tech: true, icon: 'kanban',         label: 'Projects',         path: '/tech/projects' },
        { tech: true, icon: 'plug',           label: 'Integration',      path: '/tech/integration' },
        { tech: true, icon: 'envelope',       label: 'Email Migration',  path: '/tech/email-migration' },
        { tech: true, icon: 'flower1',        label: 'Bloom Accounts',   path: '/tech/bloom-users' },
        {
          label: 'Forms',
          icon: 'ui-checks',
          submenu: [
            // Also CONFIDENTIAL_REVIEWER, who reads the answers here; for
            // someone holding only that role, this is the whole Tech group.
            { tech: true, confidentialReviewer: true, icon: 'clipboard-data', label: 'Role Alignment', path: '/tech/forms/role-alignment' },
          ],
        },
        // Hidden for now — restore when ready.
        // { tech: true, icon: 'sliders',        label: 'Config',           path: '/tech/config' },
      ],
    },
    {
      label: 'Finance',
      icon: 'cash-coin',
      submenu: [
        { finance: true, icon: 'file-earmark-bar-graph', label: 'Annuity', path: '/finance/annuity' },
      ],
    },
    {
      label: 'Entrepreneur',
      icon: 'lightbulb-fill',
      submenu: [
        // Firms (the hub directory: firms + their members) first; then what
        // the hub offers every firm; then the optional Claude add-on with its
        // own firm list, kept apart so the two never blend on one page; then
        // admin/reference.
        { entrepreneur: true, icon: 'building', label: 'Firms', path: '/entrepreneur/firms' },
        { entrepreneur: true, icon: 'signpost-split', label: 'Transitions', path: '/entrepreneur/transitions' },
        { entrepreneur: true, icon: 'megaphone', label: 'Announcements', path: '/entrepreneur/announcements' },
        {
          entrepreneur: true, icon: 'stars', label: 'Claude',
          submenu: [
            { entrepreneur: true, label: 'Claude Firms', path: '/entrepreneur/claude-firms' },
            { entrepreneur: true, label: 'Seat Requests', path: '/entrepreneur/provisioning' },
            { entrepreneur: true, label: 'Charge Usage', path: '/entrepreneur/charge-usage' },
            { entrepreneur: true, label: 'Invoices', path: '/entrepreneur/invoices' },
            { entrepreneur: true, label: 'Claude Activations', path: '/entrepreneur/claude-activations' },
          ],
        },
        { entrepreneur: true, icon: 'clock-history', label: 'Actions Log', path: '/entrepreneur/actions-log' },
        { entrepreneur: true, icon: 'question-circle', label: 'Info', path: '/entrepreneur/info' },
        { entrepreneurAdmin: true, icon: 'gear', label: 'Configuration', path: '/entrepreneur/config' },
      ],
    },
    {
      label: 'Marketing',
      icon: 'megaphone',
      submenu: [
        { marketing: true, icon: 'collection', label: 'Marketing Resources', path: '/marketing/resources' },
        // Authors the employee-facing pages under Desk → Resource Library.
        // Sits next to Marketing Resources but is a different library: that
        // one is collateral published to the external firms, these are
        // internal pages that a second checkbox can also show to the firms.
        // Was Tech → Resource Library until 2026-09-18.
        { marketing: true, icon: 'book', label: 'Resource Library', path: '/marketing/resource-library' },
        { marketing: true, icon: 'graph-up', label: 'Activity', path: '/marketing/activity' },
      ],
    },
    {
      label: 'Super Admin',
      icon: 'gear',
      submenu: [
        { admin: true, icon: 'sliders',      label: 'Admin Panel',        path: '/admin' },
        // Config is a group rather than a page because SuperAdminConfig.js is
        // already ~800 lines of toggle sections, and the flow-type definition
        // does not belong among them anyway — it is a table of decisions with
        // reasons and an audit trail, not a checklist. Same nested shape as
        // Tech → Forms; `activeLeafPath` picks the longest match, so the nested
        // path does not also highlight its parent.
        {
          label: 'Config',
          icon: 'toggles',
          submenu: [
            { admin: true, icon: 'toggles',   label: 'General',    path: '/admin/config' },
            { admin: true, icon: 'funnel',    label: 'Flow types', path: '/admin/config/flow-types' },
          ],
        },
        { admin: true, icon: 'clock-history', label: 'Document Access Log', path: '/admin/document-access-log' },
        { admin: true, icon: 'shield-lock',  label: 'Sign-in Attempts',   path: '/admin/sign-in-attempts' },
        // SUPER_ADMIN only: it reads advisors' Agentic WM conversations (every
        // read logged). Matched before the ADMIN grant below, so ADMIN alone
        // never shows it.
        { superAdmin: true, icon: 'stars', label: 'Agentic WM Activity', path: '/admin/agentic-wm' },
        { admin: true, icon: 'megaphone',    label: 'Feedback',           path: '/admin/feedback' },
        { admin: true, icon: 'speedometer', label: 'Cron',        path: '/cron' },
        { admin: true, icon: 'arrow-repeat', label: 'Celery Jobs', path: '/admin/celery-jobs' },
        { admin: true, icon: 'diagram-2',    label: 'Firms & FSC Offices', path: '/admin/firm-offices' },
        { admin: true, icon: 'person-lock',  label: 'Work Access',         path: '/admin/work-access' },
        { admin: true, icon: 'person-dash',  label: 'Non-Clients',         path: '/admin/non-clients' },
        { admin: true, icon: 'cloud-arrow-down', label: 'FSC Sync',        path: '/admin/fsc-sync' },
        { admin: true, icon: 'graph-up-arrow', label: 'Orion Sync',        path: '/admin/orion-sync' },
        { admin: true, icon: 'box-arrow-in-right', label: 'CRM Migration', path: '/admin/crm-migration' },
        { admin: true, icon: 'arrow-repeat', label: 'CRM Sync',          path: '/admin/crm-sync' },
        // Parked here 2026-09-02 pending adoption — nobody is using the
        // Executive pages yet, and a top-level group nobody opens is exactly
        // the clutter this reorganisation is removing. Still EXEC-gated, not
        // ADMIN: the role is untouched and the pages are unchanged, so
        // restoring them is lifting these three rows back into a group of
        // their own. Note an EXEC-without-ADMIN user therefore sees them under
        // a "Super Admin" heading, which is odd but harmless while the
        // audience is nobody.
        { exec: true, icon: 'bar-chart-line',  label: 'Executive KPIs',     path: '/executive/kpis' },
        { exec: true, icon: 'robot',           label: 'Executive AI Usage', path: '/executive/ai' },
        { exec: true, icon: 'flower1',         label: 'Bloom Board',        path: '/executive/bloom-board' },
      ]
    },
  ];

  // Work-side nav. Every item is `work: true` (needs WORK); `workRole` names
  // the area role it also needs (see WORK_AREA_ROLES in utils.js, granted on
  // Super Admin -> Work Access) and `beta` adds BETA for pages still being
  // built. Keep in step with the /work rules in routePermissions above.
  const workMenu = [
    {
      // Landing page for the side: a dashboard over the areas this person
      // holds. First so the Work pill's landing is always the dashboard.
      label: 'Home',
      icon: 'house-door-fill',
      path: '/work',
      work: true,
    },
    {
      // Client PORTAL users — onboarding, invites, portal status, stage, and
      // the tie to a Salesforce client. Deliberately separate from 'Salesforce Clients'
      // below: this is where clients are created and brought on, that is the
      // existing book of business. Named 'Portal Clients' (not just 'Clients')
      // since Work also lists Salesforce Clients and CRM; not 'New Clients' because
      // the page long ago stopped being only about setting them up.
      label: 'Portal Clients',
      icon: 'person-plus-fill',
      path: '/work/clients',
      work: true,
      workRole: 'isWorkPortalClients',
    },
    {
      label: 'Documents',
      icon: 'folder2-open',
      path: '/work/documents',
      work: true,
      workRole: 'isWorkDocuments',
    },
    {
      // The merging firms' client books (Redtail first), consolidated and
      // de-duplicated - the one place to see every client before the data is
      // fed into Salesforce. Office-scoped like the pages around it.
      label: 'CRM',
      icon: 'person-lines-fill',
      path: '/work/crm',
      work: true,
      workRole: 'isWorkCrm',
    },
    {
      // Pages still being built. Hidden from everyone without BETA; the group
      // itself disappears when none of them are visible.
      label: 'Beta',
      icon: 'cone-striped',
      submenu: [
        {
          // The FSC book of business (10,030 households), read from the local
          // fsc_households cache rather than live Salesforce.
          label: 'Salesforce Clients',
          icon: 'people-fill',
          path: '/work/clients/fsc',
          work: true,
          beta: true,
        },
        {
          // Net flows and AUM from Orion, attributed by firm and advisor through
          // the same FSC household join the client book uses — so it is scoped by
          // office exactly like the pages above it.
          label: 'Net New AUM',
          icon: 'graph-up-arrow',
          path: '/work/reports/aum',
          work: true,
          beta: true,
        },
        {
          // A Redtail firm's office calendar (Hammer first), read live from
          // Redtail. Same office scoping as CRM.
          label: 'CRM Calendar',
          icon: 'calendar-week',
          path: '/work/crm/calendar',
          work: true,
          workRole: 'isWorkCrm',
          beta: true,
        },
        {
          // The Prosperity Agentic Wealth Advisor - per-client conversations over
          // the firm's preset context, kept for the advisor to come back to. Also
          // opened from a client's CRM page. Same office scoping as CRM.
          label: 'Agentic WM',
          icon: 'stars',
          path: '/work/agentic-wm',
          work: true,
          workRole: 'isWorkCrm',
          beta: true,
        },
        {
          // Client service tasks: product queues shared company-wide, plus
          // each person's own list. Also opened from a client's CRM page.
          label: 'Tasks',
          icon: 'check2-square',
          path: '/work/tasks',
          work: true,
          workRole: 'isWorkTasks',
          beta: true,
        },
      ],
    },
  ];

  // Which mode the current URL belongs to. Desk and Admin membership is DERIVED
  // from each menu's own leaf paths rather than hand-kept prefix lists, so
  // adding a page to a menu is the only edit needed — a parallel list would
  // drift the first time someone forgot it.
  const inMenu = (menu) => collectLeafPaths(menu)
    .filter(Boolean)
    .some(p => location.pathname === p || location.pathname.startsWith(`${p}/`));

  const mode = isWorkMode ? 'work'
    : inMenu(deskMenu) ? 'desk'
    : inMenu(backstageMenu) ? 'backstage'
    : 'hub';

  const MENUS = { work: workMenu, desk: deskMenu, backstage: backstageMenu, hub: hubMenu };
  const menuStructure = MENUS[mode];

  // Each role grants access only to its own section. ADMIN gates the
  // Configuration menu (Admin Panel, Cron) and nothing else — an admin who
  // also wants HR/TECH/etc. access must hold those roles too.
  const hasAccess = (item) => {
    // A grouping item (carries its own submenu) is accessible when any of its
    // descendants is — lets nested groups like To-Do → AI gate correctly.
    if (item.submenu) return item.submenu.some(hasAccess);
    // Content-gated items hide when the user has nothing to see there, even if
    // their role would otherwise grant access.
    if (item.requiresContent === 'jobOpenings' && !hasViewableJobOpenings) return false;
    if (item.requiresContent === 'managedOpenings') return hasManagedOpenings;
    // Strict !== true: while the config is still loading the item stays hidden
    // rather than flashing in and then vanishing.
    if (item.requiresContent === 'yourDay' && yourDayEnabled !== true) return false;
    if (item.requiresContent === 'meetings' && meetingsEnabled !== true) return false;
    if (item.requiresContent === 'planner' && plannerEnabled !== true) return false;
    if (item.requiresContent === 'tasks' && tasksEnabled !== true) return false;
    if (item.requiresContent === 'calendar' && (calendarEnabled !== true || !calendarAllowed)) return false;
    if (item.requiresContent === 'teamRoleAlignment' && !teamRoleAlignmentEnabled) return false;
    if (item.requiresContent === 'events' && !eventsConfigured) return false;
    if (item.managerOnly)                          return isManager;
    // Work items: WORK, plus the item's area role if it names one, plus BETA if
    // it is still being built. Decided here in full — a Work beta item must
    // never fall through to the generic `beta` grant below on BETA alone.
    if (item.work) {
      if (!roleJSON.isWork) return false;
      if (item.workRole && !roleJSON[item.workRole]) return false;
      return !item.beta || Boolean(roleJSON.isBeta);
    }
    if (item.superAdmin)                          return Boolean(roleJSON.isSuperAdmin);
    if (item.admin && roleJSON.isAdmin)           return true;
    if (item.tech && roleJSON.isTech)             return true;
    if (item.hr  && roleJSON.isHR)                return true;
    if (item.compliance && roleJSON.isCompliance) return true;
    if (item.exec && roleJSON.isExec)             return true;
    if (item.finance && roleJSON.isFinance)        return true;
    if (item.fintech && roleJSON.isFintech)        return true;
    if (item.entrepreneurAdmin && roleJSON.isEntrepreneur && roleJSON.isAdmin) return true;
    if (item.entrepreneur && roleJSON.isEntrepreneur) return true;
    if (item.marketing && roleJSON.isMarketing)   return true;
    if (item.confidentialReviewer && roleJSON.isConfidentialReviewer) return true;
    // BETA is early access, not a department — it grants only the specific pilot
    // items marked `beta: true`, and those also honour their own feature flag.
    // Outside Work (whose beta pages are decided above), NOTHING carries that
    // flag right now: Your Day, Meetings, Planner, Tasks and
    // Bloom all graduated on 2026-09-02 and are `employee: true`. The mechanism
    // stays, and so do the people in the role, ready for the next pilot — see
    // api/app/feature_access.py, which is the matching switch on the server.
    if (item.beta && roleJSON.isBeta)             return true;
    if (item.employee && roleJSON.isEmployee)     return true;
    return false;
  };

  /* Where the "Desk" pill goes, and whether it is offered at all.
   *
   * The first desk item this person can actually see — NOT a fixed path.
   * Nearly everything in that mode is BETA + feature-flagged (Your Day,
   * Meetings, Planner, Tasks) or content-gated (Calendar, My Job Applicants),
   * so a hard-coded '/your-day' would send most of the company to a page the
   * route guard bounces them straight back off.
   *
   * null when nothing in the mode is visible, which hides the pill: a door to
   * an empty room is worse than no door.
   */
  const firstVisiblePath = (items) => {
    for (const item of items) {
      if (item.sectionHeader) continue;
      if (!hasAccess(item)) continue;
      if (item.submenu) {
        const nested = firstVisiblePath(item.submenu);
        if (nested) return nested;
        continue;
      }
      if (item.path) return item.path;
    }
    return null;
  };

  const deskLanding = firstVisiblePath(deskMenu);
  // Same rule for Admin: the button lands on the first console this person can
  // open, and disappears when that is none — which is most of the company.
  const backstageLanding = firstVisiblePath(backstageMenu);

  // True when the current URL is `path` itself or a sub-route of it. Plain
  // substring matching would mis-highlight siblings like /compliance/training
  // vs /compliance/training-results. For hash-anchored items (e.g.
  // '/dashboard#directory') we fall back to the old includes check —
  // location.pathname doesn't carry the hash, so the strict variant would
  // never match those.
  const pathMatches = (pathname, path) => {
    if (!path) return false;
    if (path.includes('#')) return pathname.includes(path);
    return pathname === path || pathname.startsWith(path + '/');
  };

  const isSubmenuActive = (submenu) => {
    if (!submenu) return false;
    return submenu.some(item =>
      item.submenu
        ? isSubmenuActive(item.submenu)
        : hasAccess(item) && pathMatches(location.pathname, item.path)
    );
  };

  // Of every leaf menu path that pathMatches the current URL, pick the
  // longest one — that's the single item the user actually intends to be
  // viewing. Without this, "/work" highlights on "/work/clients" because
  // it's a strict prefix; same problem for "/admin" vs "/admin/feedback".
  // Used by the renderer below in place of a raw `pathMatches` for the
  // is-this-leaf-active check.
  const activeLeafPath = (() => {
    const allLeafPaths = collectLeafPaths(menuStructure).filter(Boolean);
    let longest = null;
    for (const p of allLeafPaths) {
      if (!pathMatches(location.pathname, p)) continue;
      if (longest === null || p.length > longest.length) longest = p;
    }
    return longest;
  })();

  // Status indicators next to menu labels. Each child carries an
  // `activityKey` describing which underlying signal drives its check; the
  // parent group with the same `statusKey` rolls up — "done" only if every
  // accessible child resolves to done.
  const getItemStatus = (item) => {
    if (item.statusKey !== 'todo') return null;
    if (item.activityKey === 'claudeCert') {
      if (!claudeStatusLoaded) return null;
      return isClaudeCertified ? 'done' : 'attention';
    }
    if (item.activityKey === 'videoConfirm') {
      if (!aiActivityLoaded) return null;
      return isVideoConfirmed ? 'done' : 'attention';
    }
    if (item.activityKey === 'training') {
      if (!trainingLoaded) return null;
      return hasTrainingDue ? 'attention' : 'done';
    }
    // Generic: any To-Do leaf that carries an explicit `done` flag (e.g. each
    // granted form) flags attention until done. Such items are only built once
    // their data has loaded, so there's no false-attention-while-loading.
    if (typeof item.done === 'boolean') {
      return item.done ? 'done' : 'attention';
    }
    return null;
  };

  const getGroupStatus = (group) => {
    if (!group.statusKey) return null;
    const children = (group.submenu || []).filter(i => hasAccess(i) && i.statusKey === group.statusKey);
    if (children.length === 0) return getItemStatus(group);
    const childStatuses = children.map(getItemStatus);
    if (childStatuses.some(s => s === null)) return null;
    return childStatuses.every(s => s === 'done') ? 'done' : 'attention';
  };

  const renderStatusIcon = (status) => {
    if (status === 'done') {
      return <i className="bi bi-check-circle-fill text-success ms-2" title="Certified" aria-label="Certified"></i>;
    }
    if (status === 'attention') {
      return <i className="bi bi-exclamation-circle-fill text-warning ms-2" title="Action required" aria-label="Action required"></i>;
    }
    return null;
  };

  // Outstanding-count for an item that carries a `countKey` ("To-Do", and the
  // manager's own Team Role Alignment). Returns null when there's nothing pending
  // so a group falls back to its rolled-up status icon (the green check once
  // everything's done); a plain leaf simply shows no badge.
  const getGroupCount = (group) => {
    if (group.countKey === 'todo') return todoCount;
    if (group.countKey === 'roleAlignment') return roleAlignmentDueCount;
    return null;
  };

  // Tooltip/aria wording per count, so a badge says what it is counting
  // ("3 items to do" vs "3 assessments due from you") with its own pluralisation.
  const describeCount = (group) => (
    group.countKey === 'roleAlignment'
      ? (n) => `${n} assessment${n > 1 ? 's' : ''} due from you`
      : (n) => `${n} item${n > 1 ? 's' : ''} to do`
  );

  const renderCountBadge = (count, describe) => {
    if (!count || count <= 0) return null;
    const label = describe(count);
    return (
      <span
        className="badge rounded-pill ms-2"
        title={label}
        aria-label={label}
        style={{ background: '#d09b2c', color: '#0a2947', fontWeight: 700, fontSize: '0.7rem' }}
      >
        {count}
      </span>
    );
  };

  // Menu icon renderer. Items under the ADMIN section header drop their icon
  // in the menu (only — the pages themselves are untouched) and show a short
  // dash in its place so the listings line up with the iconned items above.
  // me-2 is the gap between an icon and its label, so it only applies when there
  // IS a label. In the collapsed rail it would offset every icon half a rem to
  // the left of centre, so it comes off — the rail centres the icons instead.
  const iconGap = isSidebarExpanded ? ' me-2' : '';

  const renderMenuIcon = (item, useDash, useBullet) => {
    // Items in a second-level submenu (a submenu nested under another submenu)
    // get a bullet, to visually mark the deeper nesting.
    if (useBullet) {
      return <span className={`menu-bullet${iconGap}`} aria-hidden="true">&bull;</span>;
    }
    if (useDash) {
      return <span className={`menu-dash${iconGap}`} aria-hidden="true">&ndash;</span>;
    }
    // No icon means no icon. The Resource Library's folders and pages take this
    // path: a rail where every row carries a glyph reads as clutter, and their
    // rows are already placed by indentation. The icon someone picks for a page
    // still shows in that page's own header, so the picker is not pointless.
    if (!item.icon) return null;
    return <i className={`bi bi-${item.icon}${iconGap}`}></i>;
  };

  /* One entry below a top-level group, at whatever depth.
   *
   * RECURSIVE because this used to be two hand-unrolled copies — a group branch
   * and a leaf branch — which meant the tree bottomed out at exactly three
   * levels. An item three deep carrying its own `submenu` fell into the leaf
   * branch, which ignores `submenu` and links to `item.path`: it rendered as a
   * dead row with its children nowhere. The Resource Library's folders are that
   * fourth level (Desk → Resource Library → Salesforce → How-Tos), and every
   * other helper here — hasAccess, isSubmenuActive, collectLeafPaths,
   * firstVisiblePath — was already recursive, so this was the one place that
   * capped the depth.
   *
   * `depth` is 1 for a direct child of the top-level group, matching what the
   * old code called `subItem`. A leaf at depth 1 keeps its icon; deeper leaves
   * get a bullet, which is the same rule the unrolled version applied.
   *
   * `menuKey` is the expand/collapse key AND the React key, and it is a PATH
   * rather than a bare label because labels are not unique across a menu:
   * deskMenu holds both To-Do → AI and Resource Library, so a folder Marketing
   * named "AI" would have shared one expanded/collapsed bit with the To-Do group
   * and the two would have opened and closed together. An item may override its
   * own segment with `item.menuKey` when even its label is not dependably
   * unique — the Resource Library's folders do, since two of them can be named
   * the same thing. Top-level groups still key on their own label: they are a
   * fixed, hand-written list.
   */
  const renderSubEntry = (item, depth, menuKey, inAdminSection) => {
    if (!hasAccess(item)) return null;

    if (item.submenu) {
      const expanded = expandedMenus[menuKey];
      const active = isSubmenuActive(item.submenu);
      return (
        <li key={menuKey} className="nav-item">
          <a
            className={`nav-link nav-link-group ${active ? 'has-active-child' : ''}`}
            href="#"
            onClick={(e) => {e.preventDefault(); toggleMenu(menuKey)}}
          >
            {renderMenuIcon(item, inAdminSection)}
            <span>{item.label}</span>
            {/* Attention dot only — no "done" check (matches the To-Do group:
                surface work, not completion). */}
            {getGroupStatus(item) === 'attention' && renderStatusIcon('attention')}
            <i className={`bi bi-chevron-${expanded ? 'down' : 'right'} ms-auto submenu-toggle`}></i>
          </a>
          {expanded && (
            <ul className="nav flex-column submenu" style={{ marginLeft: '0.75rem' }}>
              {item.submenu.map(child => renderSubEntry(
                child, depth + 1, `${menuKey}/${child.menuKey || child.label}`,
                inAdminSection))}
            </ul>
          )}
        </li>
      );
    }

    const isActive = item.path === activeLeafPath;
    return (
      <li key={menuKey} className="nav-item">
        <a
          className={`nav-link ${isActive ? 'active' : ''}`}
          href="#"
          onClick={(e) => {e.preventDefault(); sendMenuClick(item.path)}}
        >
          {renderMenuIcon(item, inAdminSection, depth >= 2)}
          <span>{item.label}</span>
          {renderStatusIcon(getItemStatus(item))}
          {/* Same count on the child row as on its parent, so an expanded group
              still says which item owes the work. */}
          {item.countKey
            && renderCountBadge(getGroupCount(item), describeCount(item))}
        </a>
      </li>
    );
  };

  // Index of a `{ sectionHeader }` divider, if a menu has one. Currently NONE
  // do: the ADMIN divider went away when the department consoles moved into
  // their own Backstage mode — the mode is the divider now. Kept because it is
  // self-guarding (-1 below) and a future menu may want a divider again. Its
  // one visual effect was rendering an en-dash in place of a group's icon for
  // everything below the divider; Backstage groups now show their real icons,
  // which is what you want when the whole mode is consoles.
  const adminHeaderIndex = menuStructure.findIndex(g => g.sectionHeader);

  const Sidebar = () => {
    // On mobile, hide the icon rail entirely when collapsed — the icons aren't
    // clickable individually (groups need the labels), so showing them is
    // misleading. Render a full-width branded header bar with the hamburger
    // tucked in; tapping it opens the full sidebar as an overlay.
    if (isMobile && !isSidebarExpanded) {
      return (
        <header
          style={{
            position: 'fixed',
            top: isImpersonating ? '2.5rem' : 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            background: '#0a2947',
            color: 'white',
            height: '3.25rem',
            display: 'flex',
            alignItems: 'center',
            padding: '0 0.75rem',
            gap: '0.75rem',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            borderBottom: '2px solid #d09b2c',
          }}
        >
          <button
            type="button"
            onClick={() => setIsSidebarExpanded(true)}
            aria-label="Open menu"
            style={{
              background: 'transparent',
              color: '#d09b2c',
              border: 'none',
              width: '2.5rem',
              height: '2.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <i className="bi bi-list" style={{ fontSize: '1.6rem' }}></i>
          </button>
          <h5 className="mb-0" style={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Prosperity <span style={{ color: '#d09b2c' }}>HUB</span>
          </h5>
        </header>
      );
    }

    return (
    <nav
      className={`sidebar ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}
      style={isMobile && isSidebarExpanded ? { zIndex: 1100 } : undefined}
    >
      {/* Collapsed, the hamburger is the header's only child — justify-content-between
          would leave it against the left edge instead of centred over the rail. */}
      <div className={`sidebar-header d-flex justify-content-${isSidebarExpanded ? 'between' : 'center'} align-items-center p-3`}>
        {isSidebarExpanded && (
          <div className="d-flex align-items-center gap-2">
            <div
              aria-hidden="true"
              style={{
                width: 32, height: 32, flexShrink: 0,
                background: '#d09b2c',
                WebkitMask: 'url(/pca.png) center / contain no-repeat',
                mask: 'url(/pca.png) center / contain no-repeat',
              }}
            />
            <h5 className="mb-0">
              Prosperity{' '}
              <span style={{ color: '#d09b2c' }}>
                {/* Always HUB. The wordmark is the PRODUCT's name, not a mode
                    indicator — it used to flip to WORK, which made the logo
                    change identity mid-session and duplicated a job the
                    highlighted pill directly below already does. */}
                HUB
              </span>
            </h5>
          </div>
        )}
        <button className="btn btn-link p-0" onClick={() => setIsSidebarExpanded(!isSidebarExpanded)} aria-label={isSidebarExpanded ? 'Collapse menu' : 'Expand menu'}>
          <i className={`bi bi-${isSidebarExpanded && isMobile ? 'x-lg' : 'list'}`}></i>
        </button>
      </div>

      {/* Mode toggle. Three modes, but the control only offers the ones this
          person actually has:

            Hub      the company — always present
            Desk     their own day — present when anything in it is visible to
                     them. Most of it (Your Day, Meetings, Planner, Tasks) is
                     BETA + feature-flagged, so for a plain employee the mode
                     can be nearly empty; offering a pill that opens an empty
                     room is worse than not offering it.
            Work     the wealth-management workspace — WORK role only

          So a typical employee sees two pills or one, and a pill is never a
          door to nothing. With one available mode the control hides entirely —
          there is nothing to switch between. */}
      {(() => {
        const modes = [
          { key: 'hub',  label: 'HR',      icon: 'house-fill',     path: '/dashboard', available: true },
          { key: 'desk', label: 'Desk',    icon: 'person-fill',       path: deskLanding, available: Boolean(deskLanding) },
          { key: 'work', label: 'Work',   icon: 'briefcase-fill', path: '/work',      available: roleJSON.isWork },
        ].filter(m => m.available);

        if (modes.length < 2) return null;

        /* NAMING: the button reads "Admin", the code says `backstage`.
           Deliberate, not drift. `admin` is already taken in this file — the
           ADMIN role (`roleJSON.isAdmin`, `item.admin`), which is a narrower
           thing than this mode and gates only the Super Admin group inside it.
           Reusing the word for the mode would make `mode === 'admin'` and
           `item.admin` sit two lines apart meaning different things. The label
           is what users read; the identifier stays unambiguous.

           Admin is NOT a fourth pill. It is a different kind of thing —
           HR/Desk/Work are where you do your job, Admin is where you
           operate the company's consoles — and a four-pill row at this width is
           cramped to the point of being unreadable. A full-width button below
           the row says "another layer" rather than "a fourth peer".

           It toggles: pressed while already in Admin, it returns you to HR.
           A button that only ever goes one way would need a second control to
           get back. */
        const backstageButton = backstageLanding && (
          <div style={{ padding: '0 0.75rem 0.6rem' }}>
            <button
              type="button"
              onClick={() => sendMenuClick(mode === 'backstage' ? '/dashboard' : backstageLanding)}
              aria-pressed={mode === 'backstage'}
              title={mode === 'backstage' ? 'Back to HR' : 'Admin — the department consoles'}
              style={{
                width: '100%',
                background: mode === 'backstage' ? '#d09b2c' : 'transparent',
                color: mode === 'backstage' ? '#0a2947' : 'rgba(255,255,255,0.78)',
                border: mode === 'backstage' ? 'none' : '1px solid rgba(255,255,255,0.22)',
                borderRadius: 6,
                padding: '0.35rem 0.5rem',
                fontWeight: 700, fontSize: '0.68rem',
                textTransform: 'uppercase', letterSpacing: '0.4px',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem',
              }}
            >
              <i className="bi bi-sliders" /> Admin
            </button>
          </div>
        );

        // One mode and no Admin areas means nothing to switch between.
        if (modes.length < 2 && !backstageButton) return null;

        return isSidebarExpanded ? (
          <>
          <div style={{ padding: '0 0.75rem 0.6rem', display: 'flex', gap: '0.3rem' }}>
            {modes.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => sendMenuClick(m.path)}
                style={{
                  flex: 1,
                  background: mode === m.key ? '#d09b2c' : 'transparent',
                  color: mode === m.key ? '#0a2947' : 'rgba(255,255,255,0.78)',
                  border: mode === m.key ? 'none' : '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 6,
                  padding: '0.35rem 0.4rem',
                  fontWeight: 700, fontSize: '0.68rem',
                  textTransform: 'uppercase', letterSpacing: '0.4px',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                }}
              >
                <i className={`bi bi-${m.icon}`} /> {m.label}
              </button>
            ))}
          </div>
            {backstageButton}
          </>
        ) : (
          /* Collapsed rail. The old two-mode version was one button that
             flipped; with three, flipping becomes a cycle nobody can predict,
             so each mode gets its own icon and the current one is highlighted. */
          <div style={{ padding: '0 0.5rem 0.6rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
            {modes.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => sendMenuClick(m.path)}
                title={`Switch to ${m.label}`}
                aria-label={`Switch to ${m.label}`}
                aria-current={mode === m.key ? 'page' : undefined}
                style={{
                  width: 36, height: 36,
                  background: mode === m.key ? '#d09b2c' : 'transparent',
                  color: mode === m.key ? '#0a2947' : 'rgba(255,255,255,0.78)',
                  border: mode === m.key ? 'none' : '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.95rem',
                }}
              >
                <i className={`bi bi-${m.icon}`} />
              </button>
            ))}
            {/* Admin gets the same icon treatment in the rail, separated by a
                hairline so it still reads as a layer below the modes rather
                than a fourth peer — the full-width button's job, in 36px. */}
            {backstageLanding && (
              <>
                <div style={{ width: 24, height: 1, background: 'rgba(255,255,255,0.22)', margin: '0.15rem 0' }} />
                <button
                  type="button"
                  onClick={() => sendMenuClick(mode === 'backstage' ? '/dashboard' : backstageLanding)}
                  title={mode === 'backstage' ? 'Back to HR' : 'Admin — the department consoles'}
                  aria-label={mode === 'backstage' ? 'Back to HR' : 'Admin'}
                  aria-pressed={mode === 'backstage'}
                  style={{
                    width: 36, height: 36,
                    background: mode === 'backstage' ? '#d09b2c' : 'transparent',
                    color: mode === 'backstage' ? '#0a2947' : 'rgba(255,255,255,0.78)',
                    border: mode === 'backstage' ? 'none' : '1px solid rgba(255,255,255,0.22)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.95rem',
                  }}
                >
                  <i className="bi bi-sliders" />
                </button>
              </>
            )}
          </div>
        );
      })()}

      <div className="sidebar-menu-wrapper" ref={setMenuWrapperRef} onScroll={onMenuScroll}>
        <ul className="nav flex-column">
          {menuStructure.map((menuGroup, groupIndex) => {
          // Section header (e.g. "ADMIN") — a label-only divider, not a link.
          // Render it only when something below it is actually accessible, so
          // employees with no role-based access never see a lonely header.
          if (menuGroup.sectionHeader) {
            const hasAccessibleItemBelow = menuStructure
              .slice(groupIndex + 1)
              .some(g => g.sectionHeader
                ? false
                : g.submenu ? g.submenu.some(hasAccess) : hasAccess(g));
            if (!hasAccessibleItemBelow) return null;
            return (
              <li key={groupIndex} className="nav-item sidebar-section-header">
                {isSidebarExpanded
                  ? <span>{menuGroup.sectionHeader}</span>
                  : <hr aria-hidden="true" />}
              </li>
            );
          }

          const hasGroupAccess = menuGroup.submenu
            ? menuGroup.submenu.some(item => hasAccess(item))
            : hasAccess(menuGroup);

          if (!hasGroupAccess) return null;

          // Role-based groups (and their descendants) sit below the ADMIN
          // header and render a long dash instead of an icon in the menu.
          const inAdminSection = adminHeaderIndex !== -1 && groupIndex > adminHeaderIndex;

          if (!menuGroup.submenu) {
            const isActive = menuGroup.path === activeLeafPath;
            return (
              <li key={groupIndex} className="nav-item">
                <a
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => {e.preventDefault(); sendMenuClick(menuGroup.path)}}
                >
                  {renderMenuIcon(menuGroup, inAdminSection)}
                  {isSidebarExpanded && <span>{menuGroup.label}</span>}
                </a>
              </li>
            );
          }

          const isExpanded = expandedMenus[menuGroup.label];
          const hasActiveChild = isSubmenuActive(menuGroup.submenu);
          // Show the pending count when there's something to do; otherwise fall
          // back to the rolled-up status icon (e.g. the green "all done" check).
          const groupCount = getGroupCount(menuGroup);

          return (
            <li key={groupIndex} className="nav-item">
              <a
                className={`nav-link nav-link-group ${hasActiveChild ? 'has-active-child' : ''}`}
                href="#"
                onClick={(e) => {e.preventDefault(); toggleMenu(menuGroup.label)}}
              >
                {renderMenuIcon(menuGroup, inAdminSection)}
                {isSidebarExpanded && (
                  <>
                    <span>{menuGroup.label}</span>
                    {/* Count-bearing groups (To-Do) show only the number when
                        there's something pending — never a "done" check. */}
                    {menuGroup.countKey
                      ? renderCountBadge(groupCount, describeCount(menuGroup))
                      : renderStatusIcon(getGroupStatus(menuGroup))}
                    <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} ms-auto submenu-toggle`}></i>
                  </>
                )}
              </a>
              {isSidebarExpanded && isExpanded && (
                <ul className="nav flex-column submenu">
                  {menuGroup.submenu.map(subItem => renderSubEntry(
                    subItem, 1, `${menuGroup.label}/${subItem.menuKey || subItem.label}`,
                    inAdminSection))}
                </ul>
              )}
            </li>
          );
          })}
        </ul>
      </div>
      {itSupportUrl && (
        <a
          href={itSupportUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="IT Support"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
            margin: isSidebarExpanded ? '0.6rem 0.75rem' : '0.6rem 0.4rem',
            background: '#d09b2c', color: '#0a2947', fontWeight: 700, fontSize: '0.85rem',
            textDecoration: 'none', padding: '0.5rem 0.75rem', borderRadius: 8,
            boxShadow: '0 1px 4px rgba(10,41,71,0.25)',
          }}
        >
          <i className="bi bi-headset"></i>
          {isSidebarExpanded && <span>IT Support</span>}
        </a>
      )}
      <div className="sidebar-footer">
        {/* One line, not two. Your name IS the account menu: it opens Settings,
            and Sign out lives in there. Logout used to sit beside it as a peer,
            which gave the footer two rows for one subject and put the single
            most destructive action in the menu permanently one stray click from
            a page you were reading. Behind a dialog it costs one extra click
            and stops being an accident. */}
        {userEmail && (
          <a
            className="nav-link"
            href="#"
            title={`${userEmail} — settings and sign out`}
            onClick={(e) => { e.preventDefault(); setShowSettings(true); }}
          >
            <i className={`bi bi-person-circle${iconGap}`}></i>
            {isSidebarExpanded && <span>{userEmail.split('@')[0]}</span>}
          </a>
        )}
        {isSidebarExpanded && <p className='rightside small mb-0'>v3.3</p>}
      </div>
    </nav>
    );
  };


  if (useSelector(selectAdminUuid)) { //Logged In
    return (
      <div style={{ minHeight: '100vh' }}>
        {!isKYBForm() && <Sidebar />}

        {/* Backdrop for the mobile-expanded sidebar — tapping anywhere outside
            the menu closes it. Without this the user can't dismiss the overlay
            unless they pick a menu item or hit the hamburger. */}
        {!isKYBForm() && isMobile && isSidebarExpanded && (
          <div
            onClick={() => setIsSidebarExpanded(false)}
            aria-hidden="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(10,41,71,0.45)',
              zIndex: 1050,
            }}
          />
        )}

        {isImpersonating && !isKYBForm() && (
          <div
            role="alert"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2000,
              background: '#d09b2c',
              color: '#0a2947',
              padding: '0.5rem 1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              fontWeight: 600,
            }}
          >
            <i className="bi bi-person-badge" style={{ fontSize: '1.1rem' }} />
            <span>
              Impersonating <strong>{userEmail || 'user'}</strong>
              {impersonatorEmail && (
                <> as <strong>{impersonatorEmail}</strong></>
              )}
            </span>
            <button
              type="button"
              onClick={handleReturnToAdmin}
              disabled={isReturning}
              style={{
                marginLeft: 'auto',
                background: '#0a2947',
                color: 'white',
                border: 0,
                borderRadius: '6px',
                padding: '0.25rem 0.75rem',
                fontWeight: 700,
              }}
            >
              {isReturning ? 'Returning…' : 'Return to admin'}
            </button>
          </div>
        )}

        <main
          className={`main-content ${isKYBForm() ? 'full-page' : contentExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'} `}
          style={{
            // On mobile the icon rail is gone — drop the 5rem reservation so
            // page content fills the viewport. Leave room at the top for the
            // fixed branded header (and the impersonation banner above it) so
            // they don't overlap page headers.
            ...(isMobile ? { marginLeft: 0, paddingLeft: 0, paddingTop: isImpersonating ? '6rem' : '3.5rem' } : {}),
            ...(isImpersonating && !isMobile ? { paddingTop: '2.25rem' } : {}),
          }}
        >
          <Outlet />
        </main>
        {!pathMatches(location.pathname, '/work') && !feedbackButtonHidden && <FeedbackWidget />}
        {/* Rendered outside <nav> so it isn't clipped by the sidebar's overflow
            or affected by the collapsed-width transition. */}
        <UserSettingsModal
          show={showSettings}
          onHide={() => setShowSettings(false)}
          userEmail={userEmail}
          onLogout={() => { setShowSettings(false); dispatch(logout()); }}
        />
      </div>

    )
  }
  else { // Not logged in
    return (
      <div>
        <Outlet />
      </div>
    );
  }
}
